import React, { useState, useEffect } from 'react'

// Importa las funciones de tu BL
import { ConnectWebSocketForOverlay, SetSocketMessageHandler, ClearSocketMessageHandler, DisconnectWebSocket } from './../../utilities/BL/Zones'
import { GetSettings } from './../../utilities/BL/Admin/Games/PluckingRooster'

// Importa el componente visual hijo
import { PluckingRooster as PluckingRoosterOverlay } from './Overlays/PluckingRooster'

/**
 * COMPONENTE: PluckingRooster OVERLAY (PARENT CONTAINER)
 * ------------------------------------------------------------------
 * Este componente es el "Cerebro" del Overlay. 
 * 1. Recupera la configuración inicial vía REST API.
 * 2. Establece la conexión WebSocket como "overlay:pluckingrooster".
 * 3. Escucha los mensajes entrantes de control ('config' y 'state').
 * 4. Controla los estados de transición (flowState) para las animaciones CSS.
 * ------------------------------------------------------------------
 */
const PluckingRooster = () => {
    
    // --- ESTADOS DE CONEXIÓN Y DATOS ---
    const [offsetY, setOffsetY] = useState(0);
    const [bannerMode, setBannerMode] = useState('invitation'); // invitation, winner, ranking
    const [winnerName, setWinnerName] = useState('');
    const [rankingList, setRankingList] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [showConfetti, setShowConfetti] = useState(false);

    // Visibilidad del flujo (idle, entering, visible, fadingOut)
    const [flowState, setFlowState] = useState('idle');

    // -------------------------------------------------------------------------
    // EFECTO: FORZAR FONDO TRANSPARENTE EN OBS
    // -------------------------------------------------------------------------
    useEffect(() => {
        const originalBodyStyle = document.body.style.backgroundColor;
        const originalHtmlStyle = document.documentElement.style.backgroundColor;
        const rootElement = document.getElementById('root');

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyStyle;
            document.documentElement.style.backgroundColor = originalHtmlStyle;
            if (rootElement) {
                rootElement.style.removeProperty('background-color');
            }
        };
    }, []);

    // -------------------------------------------------------------------------
    // EFECTO: CONTROL DE TRANSICIONES (ENTRADA / SALIDA)
    // -------------------------------------------------------------------------
    useEffect(() => {
        let timeout;
        if (flowState === 'entering') {
            timeout = setTimeout(() => setFlowState('visible'), 500);
        } else if (flowState === 'fadingOut') {
            timeout = setTimeout(() => {
                setFlowState('idle');
                setShowConfetti(false);
            }, 500);
        }
        return () => clearTimeout(timeout);
    }, [flowState]);

    const handleOverlayVisibilityChange = (visible) => {
        if (visible) {
            setFlowState((prev) => {
                if (prev === 'idle' || prev === 'fadingOut') {
                    return 'entering';
                }
                return prev;
            });
        } else {
            setFlowState((prev) => {
                if (prev !== 'idle' && prev !== 'fadingOut') {
                    return 'fadingOut';
                }
                return prev;
            });
        }
    };

    // -------------------------------------------------------------------------
    // EFECTO: CARGA INICIAL Y CONEXIÓN WEBSOCKET
    // -------------------------------------------------------------------------
    useEffect(() => {
        const init = async () => {
            // 1. Obtener la configuración inicial del servidor
            const res = await GetSettings();
            if (res.success && res.data) {
                setOffsetY(res.data.offsetY);
                setBannerMode(res.data.bannerMode);
                setWinnerName(res.data.winnerName);
                
                if (res.data.isOverlayVisible) {
                    setFlowState('visible');
                    if (res.data.bannerMode === 'winner') {
                        setShowConfetti(true);
                    }
                } else {
                    setFlowState('idle');
                }
            }

            // 2. Conectar WebSocket
            await ConnectWebSocketForOverlay(
                'pluckingrooster', 
                (status) => setConnectionStatus(status),
                (msg) => console.log('[Rooster Overlay] Toast:', msg)
            );

            // Asignamos el manejador de mensajes
            SetSocketMessageHandler(handleIncomingMessage);
        };

        const handleIncomingMessage = (message) => {
            if (!message || !message.type) return;

            const type = message.type.toLowerCase();
            const data = message.data || message;

            // Filtramos mensajes relevantes
            if (type === 'config' || type === 'plucking_rooster_config') {
                if (data.offsetY !== undefined) {
                    setOffsetY(data.offsetY);
                }
            } else if (type === 'state' || type === 'plucking_rooster_state') {
                if (data.bannerMode) {
                    setBannerMode(data.bannerMode);
                    if (data.bannerMode === 'winner' && data.isOverlayVisible) {
                        setShowConfetti(true);
                    } else {
                        setShowConfetti(false);
                    }
                }
                if (data.winnerName !== undefined) {
                    setWinnerName(data.winnerName);
                }
                if (data.rankingList !== undefined) {
                    setRankingList(data.rankingList || []);
                }
                if (data.isOverlayVisible !== undefined) {
                    handleOverlayVisibilityChange(data.isOverlayVisible);
                }
            }
            // Soporte para compatibilidad con mensajes directos del juego
            else if (type === 'plucking_rooster') {
                const { subtype, data: payload } = data;
                if (subtype === 'show_ranking') {
                    setRankingList(payload.rankingList || []);
                    handleOverlayVisibilityChange(true);
                } else if (subtype === 'hide_ranking') {
                    handleOverlayVisibilityChange(false);
                } else if (subtype === 'update_data') {
                    setRankingList(payload.rankingList || []);
                } else if (subtype === 'config_layout') {
                    if (payload.positionOffset !== undefined) setOffsetY(payload.positionOffset);
                    if (payload.layoutMode === 1) setBannerMode('ranking');
                    else setBannerMode('invitation');
                }
            }
        };

        init();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, []);

    // -------------------------------------------------------------------------
    // RENDERIZADO
    // -------------------------------------------------------------------------
    return (
        <>
            <PluckingRoosterOverlay 
                flowState={flowState}
                offsetY={offsetY}
                bannerMode={bannerMode}
                winnerName={winnerName}
                rankingList={rankingList}
                showConfetti={showConfetti}
            />

            {connectionStatus === 'error' && (
                <div style={{ position: 'fixed', bottom: 0, right: 0, background: 'red', color: 'white', padding: 2, fontSize: 8, zIndex: 99999 }}>
                    D
                </div>
            )}
        </>
    );
};

export { PluckingRooster };